import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
	FormBuilder,
	FormGroup,
	Validators,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ApiService } from 'src/app/services/api.services';
import { ActivatedRoute } from '@angular/router';
import {
	Company,
	FlattenedCompany,
	emptyFlattenedCompany,
} from 'src/app/models/data/company';
import { Country } from 'src/app/models/country';
import {
	EditCompanyRequestBody,
	UpdateCompanyRelationshipDetailsRequestBody,
} from 'src/app/models/api/requests';
import { BannerMessageService } from 'src/app/services/banner-message.service';
import {
	EntityRelationship,
	RelationshipType,
} from 'src/app/models/data/entityRelationship';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { UserSessionService } from 'src/app/services/user-session.service';
import { GeoService } from 'src/app/services/geo.service';
import { Observable, lastValueFrom, of } from 'rxjs';
import { Timezone } from 'src/app/models/timezone';
import { EditCompanyResponse } from 'src/app/models/response';
import { CompanySettingDialogComponent } from './company-about-dialog/company-about-dialog.component';
import {
	DisableAlarmIntegrationDialogComponent,
	DisableAlarmIntegrationDialogComponentData,
} from './disable-alarm-integration-dialog/disable-alarm-integration-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDivider } from '@angular/material/list';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { NgClass, AsyncPipe } from '@angular/common';
import {
	MatFormField,
	MatLabel,
	MatHint,
	MatError,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import {
	MatAutocompleteTrigger,
	MatAutocomplete,
} from '@angular/material/autocomplete';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatIcon } from '@angular/material/icon';

@Component({
	selector: 'app-company-about',
	templateUrl: './company-about.component.html',
	styleUrls: ['./company-about.component.scss', '../../../common/main.scss'],
	imports: [
		MatDivider,
		MatChipSet,
		MatChip,
		NgClass,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatSelect,
		MatOption,
		MatAutocompleteTrigger,
		MatAutocomplete,
		MatHint,
		MatSlideToggle,
		MatError,
		ReactiveFormsModule,
		MatIcon,
		MatSuffix,
		AsyncPipe,
	],
})
export class CompanyAboutComponent implements OnInit {
	private readonly id: string;
	readonly activeOptions = [
		{
			value: true,
			label: $localize`:@@companyAbout-activeOptionActive:Active`,
		},
		{
			value: false,
			label: $localize`:@@companyAbout-activeOptionInactive:Inactive`,
		},
	];
	readonly relationshipTypeOptions = [
		{
			value: 'client',
			label: $localize`:@@companyAbout-relationshipTypeOptionClient:Client`,
		},
		{
			value: 'contractor',
			label: $localize`:@@companyAbout-relationshipTypeOptionContractor:Contractor`,
		},
		{
			value: 'other',
			label: $localize`:@@companyAbout-relationshipTypeOptionOther:Other`,
		},
	];

	company!: Company;
	relationship?: EntityRelationship;
	timezone?: Timezone;
	/**
	 * Optional in-flight GET from the company-detail shell for the first About paint only.
	 * When unset (e.g. after leaving and returning to this tab), fetch via requestCompany().
	 */
	@Input() companyResponse?: Promise<{
		company: Company;
		relationship?: EntityRelationship;
		timezone?: Timezone;
	}>;
	/** Emitted after a successful company GET so the shell can refresh header/breadcrumbs. */
	@Output() companyUpdated = new EventEmitter<Company>();

	filteredCountries!: Observable<Country[]>;
	showCountryError: boolean = false;

	canEditPublicInfo = false;
	canEditPrivateInfo = false;
	canEditCompanySetting = false;
	canSaveCompanySetting = false;
	canEditCompanyName = false;

	editingPublicInfo = false;
	editingPrivateInfo = false;
	editingCompanySetting = false;
	editCompany: FlattenedCompany = emptyFlattenedCompany();

	editRelationship!: {
		relationshipType?: RelationshipType;
		utcStartDate?: string;
		utcEndDate?: string;
		active: boolean;
		externalId: string;
		notes: string;
	};

	statusActive = $localize`:@@companyAbout-statusActive:Active`;
	statusInactive = $localize`:@@companyAbout-statusInactive:Inactive`;
	settingOn = $localize`:@@companyAbout-settingOn:On`;
	settingOff = $localize`:@@companyAbout-settingOff:Off`;
	isOracle = false;
	isSentriKeyAccessUser = false;
	isEditingActiveCompany = false;
	isCompanyAdmin = false;
	hasGCM = false;
	hasGcmView = false;
	hidePwd = true;
	ajaxFormGroup!: FormGroup;

	constructor(
		private route: ActivatedRoute,
		private dialog: MatDialog,
		private apiService: ApiService,
		private bannerMessageService: BannerMessageService,
		private scService: SystemCapabilityService,
		private userSessionService: UserSessionService,
		public geoService: GeoService,
		private readonly snackbar: MatSnackBar,
		private fb: FormBuilder
	) {
		this.id = route.snapshot.params.id;

		this.canEditPrivateInfo = this.scService.hasOne([
			SystemCapabilityEnum.companyRelationshipManagement,
			SystemCapabilityEnum.globalCompanyManagement,
		]);
		// GCM View alone must not enable edit paths
		if (
			!this.scService.hasGcmAct() &&
			this.scService.has(SystemCapabilityEnum.globalCompanyManagementView)
		) {
			this.canEditPrivateInfo = this.scService.has(
				SystemCapabilityEnum.companyRelationshipManagement
			);
		}

		this.ajaxFormGroup = this.fb.group({
			ajaxUsername: ['', []],
			ajaxPassword: ['', []],
		});
		this.setupDynamicAjaxValidators();
	}

	async ngOnInit(): Promise<void> {
		if (this.companyResponse) {
			const response = await this.companyResponse;
			this.company = response.company;
			this.relationship = response.relationship;
			this.timezone = response.timezone;
		} else {
			await this.requestCompany();
		}

		const currentCompanyId =
			this.userSessionService.userSession.assignedRole?.entityRelationship
				?.parentEntityId;
		this.hasGCM = this.scService.has(
			SystemCapabilityEnum.globalCompanyManagement
		);
		this.hasGcmView = this.scService.hasGcmView();
		this.isCompanyAdmin =
			!!this.userSessionService.userSession.assignedRole?.role?.isCompanyAdmin;
		this.isEditingActiveCompany = currentCompanyId === this.company.id;
		this.isOracle = this.scService.isOracle;
		this.canEditCompanySetting =
			(this.canEditPrivateInfo && this.isOracle) ||
			(this.isCompanyAdmin && this.isEditingActiveCompany);

		const sentrilockCompanyId = await this.apiService.getSentrilockCompanyId();
		const isEditingSentrilock = this.company.id === sentrilockCompanyId;
		this.canEditCompanyName =
			currentCompanyId === sentrilockCompanyId && this.canEditPrivateInfo;
		this.isSentriKeyAccessUser = currentCompanyId === sentrilockCompanyId;

		// Edit requires GCM act (not View). Support with GCM View sees settings read-only.
		this.canEditPublicInfo =
			(!isEditingSentrilock && this.hasGCM) ||
			(this.isCompanyAdmin && this.isEditingActiveCompany);
	}

	async requestCompany(): Promise<void> {
		return this.apiService.getCompanyById(this.id).then((response) => {
			this.company = response.company;
			this.relationship = response.relationship;
			this.timezone = response.timezone;
			this.companyUpdated.emit(this.company);
		});
	}

	setEditCompanyFieldsFromCompany(): void {
		if (this.company) {
			this.editCompany = {
				id: this.company.id,
				name: this.company.name,
				taxId: this.company.taxId,
				active: this.company.active,
				timezone: this.company.timezone,
				addressId: this.company.address?.id,
				address1: this.company.address?.address1,
				address2: this.company.address?.address2,
				locality: this.company.address?.locality,
				stateProvinceRegion: this.company.address?.stateProvinceRegion,
				postalCode: this.company.address?.postalCode,
				countryCode: this.company.address?.countryCode,
				deidentification: this.company.deidentification,
				keyDetection: this.company.keyDetection,
				hideFromPublicDirectory: this.company.hideFromPublicDirectory,
				smsNotifications: this.company.smsNotifications,
				apiEnabled: this.company.apiEnabled,
				apiUsernames: Array.from(
					this.company.apiUsernames?.values() ?? []
				).join('\n'),
				ajaxEnabled: this.company.ajaxEnabled,
				ajaxUsername: this.company.ajaxUsername,
				deviceRemovalAccessOptions: this.company.deviceRemovalAccessOptions,
				requireReasonForCodeGeneration:
					this.company.requireReasonForCodeGeneration,
				contactNotifications: this.company.contactNotifications,
			};

			// Update AJAX form group with current values (password is always empty for security)
			this.ajaxFormGroup.patchValue({
				ajaxUsername: this.company.ajaxUsername || '',
				ajaxPassword: '',
			});

			// Re-setup dynamic validators with fresh company data
			this.setupDynamicAjaxValidators();
		}
	}

	setEditRelationshipFieldsFromRelationship(): void {
		if (this.relationship) {
			this.editRelationship = {
				relationshipType: this.relationship?.relationshipType ?? 'other',
				active: this.relationship?.active ?? true,
				externalId: this.relationship?.detail?.externalId ?? '',
				notes: this.relationship?.detail?.notes ?? '',
			};
		}
		// this is needed as deidentification, key detection and sms notifications are part of company
		this.setEditCompanyFieldsFromCompany();
	}

	editPublicInfoButtonClicked(): void {
		this.setEditCompanyFieldsFromCompany();
		this.editingPublicInfo = !this.editingPublicInfo;
	}

	editPublicInfoCancelButtonClicked(): void {
		this.editingPublicInfo = !this.editingPublicInfo;
	}

	async saveCompanyEdits(): Promise<EditCompanyResponse | undefined> {
		let results;
		if (this.canEditPublicInfo || this.canEditCompanySetting) {
			// we need to copy the information about the api usernames from the string field into the array to send to the server
			let apiUserNamesSet: Set<string> = new Set<string>();
			if (this.editCompany.apiEnabled) {
				apiUserNamesSet = new Set<string>(
					this.editCompany.apiUsernames?.split('\n')
				);
			}

			const requestBody: EditCompanyRequestBody = {
				id: this.editCompany.id,
				name: this.editCompany.name,
				taxId: this.editCompany.taxId,
				active: this.editCompany.active,
				timezone: this.editCompany.timezone,
				address: {
					id: this.editCompany.addressId ?? '',
					address1: this.editCompany.address1 ?? '',
					address2: this.editCompany.address2 ?? '',
					locality: this.editCompany.locality ?? '',
					stateProvinceRegion: this.editCompany.stateProvinceRegion ?? '',
					postalCode: this.editCompany.postalCode ?? '',
					countryCode: this.editCompany.countryCode ?? '',
				},
				deidentification: this.editCompany.deidentification,
				keyDetection: this.editCompany.keyDetection,
				hideFromPublicDirectory: this.editCompany.hideFromPublicDirectory,
				smsNotifications: this.editCompany.smsNotifications,
				ajaxEnabled: this.editCompany.ajaxEnabled,
				ajaxUsername: this.editCompany.ajaxUsername,
				ajaxPassword: this.editCompany.ajaxPassword,
				apiEnabled: this.editCompany.apiEnabled,
				apiUsernames: [...apiUserNamesSet],
				deviceRemovalAccessOptions: this.editCompany.deviceRemovalAccessOptions,
				requireReasonForCodeGeneration:
					this.editCompany.requireReasonForCodeGeneration,
				contactNotifications: this.editCompany.contactNotifications,
			};
			results = this.apiService.editCompanyById(this.company.id, requestBody);
		}

		return results;
	}

	async savePublicInfoEdits(): Promise<void> {
		try {
			await this.saveCompanyEdits();
			await this.requestCompany();
			this.bannerMessageService.sendMessage(
				$localize`:@@companyAbout-companyEditsSaved:Company edits have been saved.`
			);
			this.editingPublicInfo = false;
		} catch (_) {
			this.bannerMessageService.sendMessage(
				$localize`:@@companyAbout-errorSavingChanges:An error occurred while saving your changes. Please try again.`
			);
			this.editingPublicInfo = false;
		}
	}

	editPrivateInfoButtonClicked(): void {
		this.setEditRelationshipFieldsFromRelationship();
		this.editingPrivateInfo = !this.editingPrivateInfo;
	}

	editPrivateInfoCancelButtonClicked(): void {
		this.editingPrivateInfo = !this.editingPrivateInfo;
	}

	async saveRelationshipEdits(): Promise<void> {
		if (this.relationship) {
			const updRequestBody: UpdateCompanyRelationshipDetailsRequestBody = {
				id: this.relationship.id,
				childEntityId: this.relationship.childEntityId,
				parentEntityId: this.relationship.parentEntityId,
				// Since drop-down is required, this should never have to default to `other`.
				relationshipType: this.editRelationship.relationshipType ?? 'other',
				active: !!this.editRelationship.active,
				detail: {
					externalId: this.editRelationship.externalId,
					notes: this.editRelationship.notes,
				},
			};
			return this.apiService.updateCompanyRelationship(updRequestBody);
		}
	}

	async savePrivateInfoEdits(): Promise<void> {
		try {
			await this.saveRelationshipEdits();
			await this.requestCompany();
			this.bannerMessageService.sendMessage(
				$localize`:@@companyAbout-companyRelationshipEditsSaved:Company relationship edits have been saved.`
			);
			this.editingPrivateInfo = false;
		} catch (_) {
			this.bannerMessageService.sendMessage(
				$localize`:@@companyAbout-errorSavingChanges:An error occurred while saving your changes. Please try again.`
			);
			this.editingPrivateInfo = false;
		}
	}

	editCompanySettingButtonClicked(): void {
		this.setEditCompanyFieldsFromCompany();

		// Set up dynamic validators based on user role and company settings
		this.updateAjaxValidators();

		// If AJAX is enabled and user is company admin, show validation errors immediately for invalid fields
		if (this.editCompany.ajaxEnabled && this.isCompanyAdmin) {
			// Use setTimeout to ensure validators are applied first, then mark as touched
			setTimeout(() => {
				if (this.ajaxFormGroup.invalid) {
					this.markAjaxFieldsAsTouched();
				}
			}, 0);
		}

		this.editingCompanySetting = !this.editingCompanySetting;
	}

	setupDynamicAjaxValidators(): void {
		const usernameControl = this.ajaxFormGroup.get('ajaxUsername');
		const passwordControl = this.ajaxFormGroup.get('ajaxPassword');

		usernameControl?.setValidators(() => {
			if (this.isCompanyAdmin && this.editCompany.ajaxEnabled) {
				// For company admins, username is always required
				return Validators.required(usernameControl);
			} else if (this.company?.ajaxUsername) {
				// Username required if there is an existing username
				return Validators.required(usernameControl);
			}
			return null;
		});

		passwordControl?.setValidators(() => {
			if (this.isCompanyAdmin && this.editCompany.ajaxEnabled) {
				// Password is required only if there's no existing password
				if (!this.company?.ajaxUsername) {
					return Validators.required(passwordControl);
				}
			}
			return null;
		});

		// Subscribe to value changes to trigger re-validation
		usernameControl?.valueChanges.subscribe(() => {
			passwordControl?.updateValueAndValidity();
		});

		// Initial validation
		usernameControl?.updateValueAndValidity();
		passwordControl?.updateValueAndValidity();
	}

	updateAjaxValidators(): void {
		// Trigger re-validation by updating form controls
		const usernameControl = this.ajaxFormGroup.get('ajaxUsername');
		const passwordControl = this.ajaxFormGroup.get('ajaxPassword');

		usernameControl?.updateValueAndValidity();
		passwordControl?.updateValueAndValidity();
	}

	markAjaxFieldsAsTouched(): void {
		this.ajaxFormGroup.get('ajaxUsername')?.markAsTouched();
		this.ajaxFormGroup.get('ajaxPassword')?.markAsTouched();
	}

	isAjaxUsernameRequired(): boolean {
		// Always required for company admins when AJAX is enabled
		if (this.isCompanyAdmin && this.editCompany.ajaxEnabled) {
			return true;
		}
		// Also required if there is an existing username (Oracle validation)
		return !!this.company?.ajaxUsername;
	}

	isAjaxPasswordRequired(): boolean {
		// Always required for company admins when AJAX is enabled
		if (this.isCompanyAdmin && this.editCompany.ajaxEnabled) {
			return true;
		}
		// Also required if there is an existing username (Oracle validation)
		return !!this.company?.ajaxUsername;
	}

	editCompanySettingCancelButtonClicked(): void {
		this.editingCompanySetting = !this.editingCompanySetting;
	}

	async saveCompanySettingsEdits(): Promise<void> {
		if (this.company) {
			if (!this.isMatErrorVisible()) {
				try {
					const results = await this.saveCompanyEdits();
					await this.requestCompany();
					this.editingCompanySetting = false;

					if (
						this.isOracle &&
						results?.data?.apiUsersNotAdded &&
						results.data.apiUsersNotAdded.length > 0
					) {
						const alreadyTakenUsernames = Array.from(
							results.data.apiUsersNotAdded?.values() ?? []
						).join(', ');
						this.bannerMessageService.sendMessage(
							$localize`:@@companyAbout-companySettingsEditsSavedApi:Company Settings edits have been saved. Some apiUsernames were already in use: ` +
								alreadyTakenUsernames
						);
					} else {
						this.bannerMessageService.sendMessage(
							$localize`:@@companyAbout-companySettingsEditsSaved:Company Settings edits have been saved.`
						);
					}
				} catch (_) {
					this.bannerMessageService.sendMessage(
						$localize`:@@companyAbout-errorSavingChanges:An error occurred while saving your changes. Please try again.`
					);
					this.editingCompanySetting = false;
				}
			}
		}
	}

	async editCompanySettingSaveButtonClicked(): Promise<void> {
		if (!this.company) {
			return;
		}
		if (this.isMatErrorVisible()) {
			this.bannerMessageService.sendMessage(
				$localize`:@@companyAbout-companySettingsvalidationError:Company Settings has validation error(s).`
			);
			return;
		}

		if (this.editCompany.apiEnabled && this.editCompany.apiUsernames === '') {
			this.bannerMessageService.sendMessage(
				$localize`:@@companyAbout-enterApi:Please enter at least one API username.`
			);
			return;
		}

		if (this.ajaxFormGroup) {
			const ajaxFormValues = this.ajaxFormGroup.value;
			this.editCompany.ajaxUsername = ajaxFormValues.ajaxUsername;
			this.editCompany.ajaxPassword = ajaxFormValues.ajaxPassword;
		}

		// For company admins, AJAX fields are required
		if (
			this.editCompany.ajaxEnabled &&
			this.isCompanyAdmin &&
			(this.isInvalidString(this.editCompany.ajaxUsername) ||
				(this.isInvalidString(this.editCompany.ajaxPassword) &&
					!this.company.ajaxUsername))
		) {
			const usernameInvalid = this.isInvalidString(
				this.editCompany.ajaxUsername
			);
			const passwordInvalid =
				this.isInvalidString(this.editCompany.ajaxPassword) &&
				!this.company.ajaxUsername;

			if (usernameInvalid && passwordInvalid) {
				this.bannerMessageService.sendMessage(
					$localize`:@@companyAbout-ajaxUsernameAndPasswordRequired:Please enter an AJAX username and password.`
				);
			} else if (usernameInvalid) {
				this.bannerMessageService.sendMessage(
					$localize`:@@companyAbout-ajaxUsernameRequired:Please enter an AJAX username.`
				);
			} else if (passwordInvalid) {
				this.bannerMessageService.sendMessage(
					$localize`:@@companyAbout-ajaxPasswordRequired:Please enter an AJAX password.`
				);
			}
			return;
		}

		// For SentriKeyAccess users, username and password are only required if there is an existing username or the username is provided
		if (
			this.editCompany.ajaxEnabled &&
			this.isSentriKeyAccessUser &&
			((this.company.ajaxUsername &&
				this.isInvalidString(this.editCompany.ajaxUsername)) ||
				(this.isInvalidString(this.editCompany.ajaxPassword) &&
					!this.company.ajaxUsername &&
					!this.isInvalidString(this.editCompany.ajaxUsername)))
		) {
			if (
				this.company.ajaxUsername &&
				this.isInvalidString(this.editCompany.ajaxUsername)
			) {
				this.bannerMessageService.sendMessage(
					$localize`:@@companyAbout-ajaxUsernameRequired:Please enter an AJAX username.`
				);
			} else if (
				this.isInvalidString(this.editCompany.ajaxPassword) &&
				!this.company.ajaxUsername &&
				!this.isInvalidString(this.editCompany.ajaxUsername)
			) {
				this.bannerMessageService.sendMessage(
					$localize`:@@companyAbout-ajaxPasswordRequired:Please enter an AJAX password.`
				);
			} else {
				this.bannerMessageService.sendMessage(
					$localize`:@@companyAbout-ajaxInputValidationError:Please enter an AJAX username and password.`
				);
			}
			return;
		}

		// Opens a dialog for editing company settings and saves the changes if the dialog result is truthy.
		await lastValueFrom(
			this.dialog
				.open(CompanySettingDialogComponent, {
					panelClass: 'company-setting-dialog',
					data: {},
				})
				.afterClosed()
		).then((confirmClicked: any) => {
			if (confirmClicked) {
				this.saveCompanySettingsEdits();
			}
		});
	}

	isInvalidString(str?: string): boolean {
		return typeof str !== 'string' || str!.trim() === '';
	}

	private _filter(value: string): Country[] {
		const filterValue = value.toLowerCase();
		return this.geoService.allCountries.filter((country) =>
			country.name.toLowerCase().includes(filterValue)
		);
	}

	filterCountries(value: any): void {
		this.filteredCountries = of(this._filter(value));
	}

	displayCountryName = (twoLetterCode: string): string => {
		let foundCountry = null;
		if (this.geoService?.allCountries) {
			foundCountry = this.geoService.allCountries.find(
				(country) => country.twoLetterCode === twoLetterCode
			);
		}

		return foundCountry && foundCountry.name ? foundCountry.name : '';
	};

	displayStatusName(status: boolean): string {
		if (status) {
			return this.statusActive;
		} else {
			return this.statusInactive;
		}
	}

	getChipStyling(status: boolean): string {
		if (status) {
			return 'status-chip-active';
		} else {
			return 'status-chip-inactive';
		}
	}

	get displayTimezone(): string {
		// Timezone is currently read only, if that changes this has to change
		if (this.timezone?.timezone) {
			return this.editCompany.timezone + ' - ' + this.timezone?.description;
		} else {
			return this.editCompany.timezone ?? '';
		}
	}

	set displayTimezone(timezone: string) {
		// Timezone is currently read only, if that changes this has to change
		this.editCompany.timezone = this.editCompany.timezone;
	}

	private isMatErrorVisible(): boolean {
		const matError = document.querySelector('mat-error');
		return !!matError && matError.clientHeight !== 0;
	}

	openAutocompleteCountries() {
		this.filteredCountries = of(this.geoService.allCountries);
	}

	countrySelected() {
		this.showCountryError = false;
	}

	countryInput() {
		this.showCountryError = true;
	}

	getBannerMessageService() {
		return this.bannerMessageService;
	}

	onAjaxToggleChange(value: boolean): void {
		// Ignore if the value changes to TRUE
		if (value === true) return;
		// Ignore if the value was already FALSE before
		if (this.company.ajaxEnabled === false) return;

		// If the value changes to FALSE
		const data: DisableAlarmIntegrationDialogComponentData = {
			companyId: this.company.id,
		};
		const dialogRef: MatDialogRef<DisableAlarmIntegrationDialogComponent> =
			this.dialog.open(DisableAlarmIntegrationDialogComponent, {
				maxWidth: '510px',
				panelClass: 'pop-up-dialog-v3',
				data: data,
			});
		dialogRef.afterClosed().subscribe(async (result: any) => {
			if (!!result) {
				this.snackbar.open(
					$localize`:@@companyAbout-confirmAjaxRemoveSuccess:AJAX Successfully Disabled`,
					undefined,
					{
						duration: 2000,
					}
				);
				this.company.ajaxEnabled = false;
			} else {
				this.editCompany.ajaxEnabled = true;
			}
		});
	}

	isContactNotificationsEnabled(): boolean {
		// Contact Notifications can only be enabled for Care company type
		return this.company?.companyType === 'care';
	}

	hasAlertsAndNotificationsContent(): boolean {
		return (
			this.isOracle ||
			(this.company?.keyDetection && !this.isOracle) ||
			this.isSentriKeyAccessUser ||
			(this.isEditingActiveCompany && !!this.company?.contactNotifications)
		);
	}

	hasDeviceManagementContent(): boolean {
		return (
			this.isOracle ||
			(this.isEditingActiveCompany &&
				(!!this.company?.deviceRemovalAccessOptions ||
					this.company?.requireReasonForCodeGeneration !== undefined))
		);
	}

	hasSystemIntegrationsContent(): boolean {
		return (
			this.isSentriKeyAccessUser ||
			(this.isEditingActiveCompany && !!this.company?.ajaxEnabled) ||
			this.isOracle
		);
	}
}
